import './home.scss';

import {
	type InventoryData,
	InventoryRateLevelWidget,
	type View,
} from './components/inventory-rate-level-widget/inventory-rate-level-widget';
import {
	type OccupancyData,
	OccupancyWidget,
} from './components/occupancy-widget/occupancy-widget';

import { today } from 'features/dates/date-helpers';
import { useUser } from 'features/users/context/userContext';
import { calcTotalForecast } from 'pages/MyView/tableCustomCellRender';
import type { RecReviewData } from 'pages/MyView/useDashboardController';
import { useState } from 'react';
import { HomeDashboardControls } from './components/home-dashboard-controls/home-dashboard-controls';
import { OtbWidget } from './components/otb-widget/otb-widget';
import { OverviewPane } from './components/overview-pane/overview-pane';
import { RecRateWidget } from './components/rec-rate-widget/rec-rate-widget';
import { TrendWidget } from './components/trend-widget/trend-widget';
import { useMetricsForDate } from './hooks/use-metrics-for-date';
import { useRateLevelsForDate } from './hooks/use-rate-levels-for-date';
import { useTrendDataForDate } from './hooks/use-trend-data-for-date';

export const Home = () => {
	const { user } = useUser();
	const [date, setDate] = useState(today());
	const { metrics, loading: loadingMetrics } = useMetricsForDate(date);
	const {
		rateLevels,
		total,
		loading: loadingRateLevels,
	} = useRateLevelsForDate(date);
	const trendData = useTrendDataForDate(date);

	const [invRateLevelView, setView] = useState<View>('inventory');
	const calcForecast: Partial<RecReviewData> | undefined = metrics && {
		group_booked: metrics.group_booked,
		group_booked_live: metrics.group_booked_live!,
		total_fcst: metrics.total_fcst,
		total_capacity: metrics.total_capacity,
		stay_date: metrics.stay_date,
	};

	const finalForecastValue = calcTotalForecast(calcForecast);

	const occupancyData: OccupancyData | undefined = metrics && {
		lastYearFinalPct: metrics.occ_pct_lyf,
		forecastPct:
			finalForecastValue &&
			(finalForecastValue.fcst_occ_pct_live
				? finalForecastValue.fcst_occ_pct_live
				: finalForecastValue.fcst_occ_pct),
		occupancyPct: metrics.occ_pct_live ?? metrics.occ_pct,
	};

	const inventoryData: InventoryData | undefined = metrics && {
		groupBlocked: metrics.group_blocked,
		groupBlockedLive: metrics.group_blocked_live,
		groupBooked: metrics.group_booked,
		groupBookedLive: metrics.group_booked_live,
		outOfOrder: metrics.out_of_order,
		outOfOrderLive: metrics.ooo_live,
		transientBooked: metrics.transient_booked,
		transientBookedLive: metrics.transient_booked_live,
		physicalCap: metrics.total_capacity,
	};

	return (
		<div className='tw-w-full tw-h-[700px] md:tw-h-full tw-p-2 tw-px-3 md:tw-p-6 md:tw-pr-8 md:tw-pl-4 tw-box-border tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_3fr] tw-gap-4 md:tw-absolute md:tw-top-0 md:tw-left-0 md:tw-min-w-[1096px]'>
			<OverviewPane
				name={user?.first_name}
				selectedDate={date}
				setDate={setDate}
				setView={setView}
				lastUpdated={metrics?.last_updated_date}
			/>
			<div className='tw-hidden md:tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-12 md:tw-grid-rows-[2fr_3fr_6fr]'>
				<div className='md:tw-col-span-12 tw-self-end'>
					<HomeDashboardControls
						selectedDate={date}
						setDate={setDate}
						setView={setView}
						eventId={metrics?.event_id}
						groupBlocked={metrics?.group_blocked_live ?? metrics?.group_blocked}
						groupBooked={metrics?.group_booked_live ?? metrics?.group_booked}
					/>
				</div>
				<div className='md:tw-col-span-5'>
					<RecRateWidget metrics={metrics} loading={loadingMetrics} />
				</div>
				<div className='md:tw-col-span-4'>
					<OtbWidget
						rateLevels={rateLevels}
						total={total}
						loading={loadingRateLevels}
					/>
				</div>
				<div className='md:tw-col-span-3'>
					<OccupancyWidget
						occupancyData={occupancyData}
						loading={loadingMetrics}
					/>
				</div>
				<div className='md:tw-col-span-7'>
					<TrendWidget trendData={trendData.data} loading={trendData.loading} />
				</div>
				<div className='md:tw-col-span-5'>
					<InventoryRateLevelWidget
						view={invRateLevelView}
						setView={setView}
						inventoryData={inventoryData}
						rateLevels={rateLevels}
						loadingMetrics={loadingMetrics}
						loadingRateLevels={loadingRateLevels}
					/>
				</div>
			</div>
		</div>
	);
};
